import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import ShareIcon from '@mui/icons-material/Share';
import { Button, Slider } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { Map, Pin, useMap } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import useGeolocation from "react-hook-geolocation";
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useShareHandler } from '../../../hooks/common/useShareHandler';
import { useFavoriteIds } from '../../../hooks/favorites/useFavoriteIds';
import { useGetFavorites } from '../../../hooks/favorites/useGetFavorites';
import { useToggleFavorite } from '../../../hooks/favorites/useToggleFavorite';
import { useMapInitialState } from '../../../hooks/map/useMapInitialState';
import { useDeleteMedia } from '../../../hooks/media/useDeleteMedia';
import { useToggleLike } from '../../../hooks/media/useToggleLike';
import { useUpdateBoundingMediaPoints } from '../../../hooks/media/useUpdateBoundingMediaPoints';
import { useMediaPlayer } from '../../../hooks/mediaplayer/useMediaPlayer';
import { useReportMedia } from '../../../hooks/media/useReportMedia';
import { useBlockMedia } from '../../../hooks/media/useBlockMedia';
import CommonPage from "../common/CommonPage";
import MediaGrid from '../common/MediaGrid';
import VideoPlayer from '../common/VideoPlayer';
import AdvancedMarkerWithRef from './AdvancedMarkerWithRef';
import { useBlockUser } from '../../../hooks/media/useBlockUser';

const mapDefaultProps = {
	zoom: 8,
	center: { lat: 55.7825203, lng: 37.7917714 },
};

const MIN = 0;
const MAX = 10;
const marks = [{ value: MIN, label: '' }, { value: MAX, label: '' }];

const useThrottleWithTrailing = (callback, delay) => {
	const lastRun = useRef(0);
	const timeoutRef = useRef(null);
	const stopTimeoutRef = useRef(null);
	const lastArgs = useRef(null);
	return useCallback((...args) => {
		const now = Date.now();
		const sinceLast = now - lastRun.current;
		lastArgs.current = args;
		const invoke = () => {
			callback(...lastArgs.current);
			lastRun.current = Date.now();
			timeoutRef.current = null;
		};
		if (sinceLast >= delay) {
			invoke();
		} else if (!timeoutRef.current) {
			timeoutRef.current = setTimeout(invoke, delay - sinceLast);
		}
		if (stopTimeoutRef.current) {
			clearTimeout(stopTimeoutRef.current);
		}
		stopTimeoutRef.current = setTimeout(() => {
			const timeSinceLastCall = Date.now() - lastRun.current;
			if (timeSinceLastCall >= 100) {
				invoke();
			}
		}, 120);
	}, [callback, delay]);
};

const MapController = ({ initialState, geolocation, handleShare, onBoundsChanged }) => {
	const map = useMap();
	const debouncedUpdateMediaPoints = useThrottleWithTrailing(onBoundsChanged, 500);

	useEffect(() => {
		if (!map || !initialState) return;
		map.moveCamera({ center: initialState.center, zoom: initialState.zoom });
	}, [map, initialState]);

	useEffect(() => {
		if (!map) return;

		const boundsListener = map.addListener('bounds_changed', () => {
			const bounds = map.getBounds();
			if (bounds) {
				const boundsDetail = {
					north: bounds.getNorthEast().lat(),
					south: bounds.getSouthWest().lat(),
					east: bounds.getNorthEast().lng(),
					west: bounds.getSouthWest().lng(),
				};
				debouncedUpdateMediaPoints(boundsDetail);
			}
		});

		const idleListener = map.addListener('idle', () => {
			if (!map.getCenter()) return;
			const center = map.getCenter().toJSON();
			const zoom = map.getZoom();
			const stateToSave = { center, zoom };
			localStorage.setItem('lastMapLocation', JSON.stringify(stateToSave));
		});

		return () => {
			window.google.maps.event.removeListener(boundsListener);
			window.google.maps.event.removeListener(idleListener);
		};
	}, [map, debouncedUpdateMediaPoints]);

	useEffect(() => {
		if (!map) return;

		const REFRESH_INTERVAL = 1000;

		const intervalId = setInterval(() => {
			const bounds = map.getBounds();
			if (bounds) {
				const boundsDetail = {
					north: bounds.getNorthEast().lat(),
					south: bounds.getSouthWest().lat(),
					east: bounds.getNorthEast().lng(),
					west: bounds.getSouthWest().lng(),
				};
				onBoundsChanged(boundsDetail);
			}
		}, REFRESH_INTERVAL);

		return () => {
			clearInterval(intervalId);
		};
	}, [map, onBoundsChanged]);

	const handleGoToMyLocation = () => {
		if (geolocation.latitude && geolocation.longitude && map) {
			map.moveCamera({ center: { lat: geolocation.latitude, lng: geolocation.longitude }, zoom: 12 });
		}
	};

	const handleWebShare = () => {
		if (!map) return;
		const center = map.getCenter().toJSON();
		const zoom = map.getZoom();
		const shareUrl = `https://nowahere.com/map?lat=${center.lat}&lng=${center.lng}&zoom=${zoom}`;
		handleShare(shareUrl);
	};

	return (
		<>
			<Button
				onClick={handleGoToMyLocation}
				disabled={!map || !geolocation.latitude}
				sx={{
					position: 'absolute', top: 10, right: 10, zIndex: 1, minWidth: 0,
					padding: '10px', borderRadius: '50%', backgroundColor: 'white',
					boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
					'&:hover': { backgroundColor: '#f5f5f5' }
				}}
			>
				<MyLocationIcon sx={{ color: '#5f6368' }} />
			</Button>
			<Button
				onClick={handleWebShare}
				disabled={!map}
				sx={{
					position: 'absolute', bottom: 10, left: 10, zIndex: 1, minWidth: 0,
					padding: '10px', borderRadius: '50%', backgroundColor: 'white',
					boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
					'&:hover': { backgroundColor: '#f5f5f5' },
					'&.Mui-disabled': { backgroundColor: '#f0f0f0' }
				}}
			>
				<ShareIcon sx={{ color: !map ? '#bdbdbd' : '#5f6368' }} />
			</Button>
		</>
	);
};

const GoogleMapsTestPage = () => {
	const location = useLocation();
	const geolocation = useGeolocation({
		enableHighAccuracy: true,
		timeout: 15000,
		maximumAge: 0
	});

	const initialState = useMapInitialState(location.search, geolocation, mapDefaultProps);

	const [val, setVal] = useState(1);

	useGetFavorites();
	const favoriteIds = useFavoriteIds();
	const { toggleFavorite } = useToggleFavorite();
	const { toggleLike } = useToggleLike();
	const { handleDeleteMedia } = useDeleteMedia();
	const { handleReportMedia } = useReportMedia();
	const { handleBlockMedia } = useBlockMedia();
	const { handleBlockUser } = useBlockUser();
	const { selectedMediaId, currentVideoUrl, playerMessage, handleMediaSelect } = useMediaPlayer(null);
	const { handleShare, ShareSnackbar } = useShareHandler();
	const updateMediaPoints = useUpdateBoundingMediaPoints();
	const mediaPoints = useSelector((state) => state.media.points);

	const handleChange = (e, newValue) => {
		e.preventDefault();
		setVal(newValue);
	};

	function valuetext(value) {
		return `${value} day before`;
	}

	const onMouseEnter = (marker) => console.log(`Mouse enter: ${marker}`);
	const onMouseLeave = (marker) => console.log(`Mouse leave: ${marker}`);

	const getFilteredMediaPoints = (mediaPoints, sliderValue) => {
		if (sliderValue === 0) {
			return mediaPoints.filter(mediaPoint => mediaPoint.status === 'STREAM');
		} else {
			const now = new Date();
			const daysAgo = sliderValue;
			const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
			return mediaPoints.filter(mediaPoint => new Date(mediaPoint.startedTimestamp) >= cutoffDate);
		}
	};

	const finalMediaPoints = useMemo(() => {
		const filteredPoints = getFilteredMediaPoints(mediaPoints, val);

		if (filteredPoints.length >= 10) {
			return filteredPoints;
		}

		const neededCount = 10 - filteredPoints.length;

		const filteredIds = new Set(filteredPoints.map(p => p.id));

		const additionalPoints = mediaPoints
			.filter(p => !filteredIds.has(p.id))
			.sort((a, b) => new Date(b.startedTimestamp) - new Date(a.startedTimestamp));

		const pointsToAdd = additionalPoints.slice(0, neededCount);

		return [...filteredPoints, ...pointsToAdd];
	}, [mediaPoints, val]);

	const getMarkerZIndex = (mediaPoint, selectedMediaId) => {
		if (mediaPoint.id === selectedMediaId) return 1000;
		if (mediaPoint.status === 'STREAM') return 100;
		return 1;
	};


	return (
		<CommonPage>
			{ShareSnackbar}
			<Grid container spacing={4}>
				<Grid size={12}>
					<Grid container spacing={2}>
						<Grid size={8} sx={{ display: 'flex', flexDirection: 'column' }}>
							<div style={{ height: '45vh', width: '100%', position: 'relative' }}>
								{initialState ? (
									<Map
										defaultCenter={initialState.center}
										defaultZoom={initialState.zoom}
										disableDefaultUI
										mapId="han-app-dev"
										zoomControl
									>
										<MapController
											initialState={initialState}
											geolocation={geolocation}
											handleShare={handleShare}
											onBoundsChanged={updateMediaPoints}
										/>

										{finalMediaPoints.map(mediaPoint => (
											<AdvancedMarkerWithRef
												key={mediaPoint.id}
												onMarkerClick={() => handleMediaSelect(mediaPoint)}
												onMouseEnter={onMouseEnter}
												onMouseLeave={onMouseLeave}
												position={{
													lat: mediaPoint.startGeoLocation.latitude,
													lng: mediaPoint.startGeoLocation.longitude
												}}
												zIndex={getMarkerZIndex(mediaPoint, selectedMediaId)}
											>
												<Pin
													background={selectedMediaId === mediaPoint.id ? '#2196F3' : (mediaPoint.status === 'STREAM' ? '#4CAF50' : null)}
													borderColor={selectedMediaId === mediaPoint.id ? '#1976D2' : (mediaPoint.status === 'STREAM' ? '#388E3C' : null)}
													glyphColor={selectedMediaId === mediaPoint.id ? '#0D47A1' : (mediaPoint.status === 'STREAM' ? '#2E7D32' : null)}
												/>
											</AdvancedMarkerWithRef>
										))}
									</Map>
								) : (
									<div>Loading map...</div>
								)}
							</div>

							<Grid container spacing={2} sx={{ alignItems: 'center', height: '5vh' }}>
								<Grid><OnlinePredictionIcon /></Grid>
								<Grid size="grow">
									<Slider
										aria-label="Timeline"
										getAriaValueText={valuetext}
										onChange={handleChange}
										step={1}
										value={val}
										marks={marks}
										min={MIN}
										max={MAX}
										valueLabelDisplay="auto"
									/>
								</Grid>
								<Grid><AccessTimeIcon /></Grid>
							</Grid>
						</Grid>

						<Grid size={4} sx={{ height: '50vh', margin: 'auto 0' }}>
							<VideoPlayer url={currentVideoUrl} message={playerMessage} />
						</Grid>
					</Grid>
				</Grid>
				<Grid size={12}>
					<MediaGrid
						mediaPoints={finalMediaPoints}
						onCardClick={handleMediaSelect}
						selectedMediaId={selectedMediaId}
						favoriteIds={favoriteIds}
						onToggleFavorite={toggleFavorite}
						onToggleLike={toggleLike}
						onShareClick={handleShare}
						onDeleteClick={handleDeleteMedia}
						onReportClick={handleReportMedia}
						onBlockClick={handleBlockMedia}
						onBlockUserClick={handleBlockUser}
						gridItemProps={{ xs: 12, sm: 6, md: 3, lg: 2 }}
					/>
				</Grid>
			</Grid>
		</CommonPage>
	);
}

export default GoogleMapsTestPage;